import { Injectable } from '@angular/core';
import isObject from 'lodash-es/isObject';
import { of } from 'rxjs/observable/of';
import { GeolocationService } from '../geolocation/geolocation.service';
import { TestObject } from 'protractor/built/driverProviders';

enum Affiliation {
  STD_GST = 'STD_GST',
  FL_RESIDENT = 'FL_RESIDENT',
  STANDARD_GUEST = 'STANDARD_GUEST',
  CALIFORNIA_RESIDENT = 'CALIFORNIA_RESIDENT',
  BAJA_RESIDENT = 'BAJA_RESIDENT',
  SOUTHERN_CALIFORNIA_RESIDENT = 'SOUTHERN_CALIFORNIA_RESIDENT',
  FLORIDA_RESIDENT = 'FLORIDA_RESIDENT',
  UK_RESIDENT = 'UK_RESIDENT',
  CANADA_RESIDENT = 'CANADA_RESIDENT',
  TEXAS_RESIDEN = 'TEXAS_RESIDEN'
}

export interface StoredAffiliation {
  storedAffiliations?: String[];
}
export interface SiteAffiliations {
  wdw?: StoredAffiliation;
  dlr?: StoredAffiliation;
  dcl?: StoredAffiliation;
  pep?: StoredAffiliation;
}

@Injectable({
  providedIn: 'root'
})
export class AffiliationsService {

  affiliationsJar = 'AFFILIATIONS_jar';

  defaultAffiliations: SiteAffiliations = {
    dlr: { storedAffiliations: [Affiliation.STD_GST] },
    wdw: { storedAffiliations: [Affiliation.STD_GST] },
    pep: { storedAffiliations: [Affiliation.STD_GST] },
  };

  WEST_USA_RESIDENT = 'WEST_USA_RESIDENT';
  EAST_USA_RESIDENT = 'EAST_USA_RESIDENT';
  WEST_CANADA_RESIDENT = 'WEST_CANADA_RESIDENT';
  FLORIDA_RESIDENT = 'FL_RESIDENT';
  TEXAS_RESIDENT = 'TX_RESIDENT';
  CALIFORNIA_RESIDENT = 'CAL_RESIDENT';
  BAJA_RESIDENT = 'BAJA_RESIDENT';
  SOUTHERN_CALIFORNIA_RESIDENT = 'SOCAL_RESIDENT';
  UK_RESIDENT = 'UK_RESIDENT';
  CANADA_RESIDENT = 'CA_RESIDENT';
  STANDARD_GUEST = 'STD_GST';
  dcl = {
      // USA
      florida: this.FLORIDA_RESIDENT,
      washington: this.WEST_USA_RESIDENT,
      utah: this.WEST_USA_RESIDENT,
      oregon: this.WEST_USA_RESIDENT,
      nevada: this.WEST_USA_RESIDENT,
      'new mexico': this.WEST_USA_RESIDENT,
      idaho: this.WEST_USA_RESIDENT,
      colorado: this.WEST_USA_RESIDENT,
      california: this.WEST_USA_RESIDENT,
      arizona: this.WEST_USA_RESIDENT,
      texas: this.TEXAS_RESIDENT,
      'new york': this.EAST_USA_RESIDENT,
      pennsylvania: this.EAST_USA_RESIDENT,
      connecticut: this.EAST_USA_RESIDENT,
      'district of columbia': this.EAST_USA_RESIDENT,
      massachusetts: this.EAST_USA_RESIDENT,
      maryland: this.EAST_USA_RESIDENT,
      'rhode island': this.EAST_USA_RESIDENT,
      oklahoma: this.TEXAS_RESIDENT,
      louisiana: this.TEXAS_RESIDENT,
      hawaii: this.WEST_USA_RESIDENT,
      montana: this.WEST_USA_RESIDENT,
      maine: this.EAST_USA_RESIDENT,
      'new hampshire': this.EAST_USA_RESIDENT,
      vermont: this.EAST_USA_RESIDENT,
      'new jersey': this.EAST_USA_RESIDENT,
      delaware: this.EAST_USA_RESIDENT,

      // Canada
      alberta: this.WEST_CANADA_RESIDENT,
      'british columbia': this.WEST_CANADA_RESIDENT,
      manitoba: this.WEST_CANADA_RESIDENT,
      saskatchewan: this.WEST_CANADA_RESIDENT,
      priorityOrder: null
  };
  dlr = {
      // used for DLR content
      california: this.CALIFORNIA_RESIDENT,
      'baja california': this.BAJA_RESIDENT,
      // Zip code range
      CAL_RESIDENT: {
          zipMin: 90000,
          zipMax: 93599,
          affiliation: this.SOUTHERN_CALIFORNIA_RESIDENT
      },
      priorityOrder: [
          'standard',
          'cal_resident',
          'socal_resident'
      ]
  };
  wdw = {
      // used for WDW content
      florida: this.FLORIDA_RESIDENT,
      'united kingdom': this.UK_RESIDENT,
      canada: this.CANADA_RESIDENT,
      priorityOrder: [
          'standard',
          'fl_resident',
          'canada_resident'
      ]
  };
  pep = Object.assign(this.dlr, this.wdw);

  AFFILIATIONS = {
    'dcl': this.dcl,
    'dlr': this.dlr,
    'wdw': this.wdw,
    'pep': this.pep
  };
  AFFILIATION_STRINGS = {
    'STANDARD': this.STANDARD_GUEST,
    'CALIFORNIA': this.CALIFORNIA_RESIDENT,
    'BAJA': this.BAJA_RESIDENT,
    'SOCAL': this.SOUTHERN_CALIFORNIA_RESIDENT,
    'FLORIDA': this.FLORIDA_RESIDENT,
    'UNITED_KINGDOM': this.UK_RESIDENT,
    'CANADA': this.CANADA_RESIDENT,
    'TEXAS': this.TEXAS_RESIDENT
  };

  affiliationList = this.AFFILIATION_STRINGS;
  affiliationPriorityList = this.AFFILIATIONS['dcl'].priorityOrder;

  setAffiliations = {};

  constructor(private geolocationService: GeolocationService) { }

  getPriorityAffiliations () {
    return this.affiliationPriorityList;
  }

  getAffiliations(siteBrand = 'dcl') {
    const storage = window.sessionStorage;
    let affiliationsJarData = {};
    let storageAvailable = false;

    // Check if storage is available
    if (storage) {
      try {
        storageAvailable = storage.storageAvailable = true;
      } catch (error) {
          // fail silently
      }
    }

    // If storage is available and affil in storage pull data
    if (storageAvailable && storage[this.affiliationsJar]) {
        affiliationsJarData = JSON.parse(storage[this.affiliationsJar]);
    }

    if (isObject(affiliationsJarData) && affiliationsJarData[siteBrand]) {
      // Affiliation data is set and brand exists
      return Promise.resolve(affiliationsJarData);
    } else {
      const self = this;
      const affiliationData = this.defaultAffiliations[siteBrand] || [];
      const brandAffiliations = this.AFFILIATIONS[siteBrand] || {};

      return this.getGeolocation(brandAffiliations, affiliationData).then(() => {
        if (self.setAffiliations[siteBrand]) {
          return self.setAffiliations[siteBrand](affiliationData);
        } else {
          return Promise.resolve();
        }
      }).then(() => {
        // Add siteBrand affiliationData to json object
        affiliationsJarData[siteBrand] = {
          'storedAffiliations': affiliationData
        };

        // Change object to string and place in sessionStorage
        storage[this.affiliationsJar] =  JSON.stringify(affiliationsJarData);

        return affiliationsJarData;
      });
    }
  }

  getGeolocation(possibleAffiliations, affiliations) {
    return new Promise(resolve => {
      this.geolocationService.getGeoCode().subscribe(function(geodata) {
        const region = geodata.state || geodata.region;
        if (possibleAffiliations.hasOwnProperty(region)) {
          const state = possibleAffiliations[region];
          affiliations.push(state);
          if (
            possibleAffiliations[state] &&
            possibleAffiliations[state].zipMin &&
            possibleAffiliations[state].zipMax &&
            possibleAffiliations[state].affiliation
          ) {
              const zip = geodata.zip || geodata.zipCode,
                  zipMin = possibleAffiliations[state].zipMin,
                  zipMax = possibleAffiliations[state].zipMax;

              if (zip >= zipMin && zip <= zipMax) {
                affiliations.push(possibleAffiliations[state].affiliation);
              }
          }
        } else if (possibleAffiliations.hasOwnProperty(geodata.country)) {
          affiliations.push(possibleAffiliations[geodata.country]);
        }

        resolve();
      });
    });
}

}
